<template>
    <div class="w-full h-full">
        <div class="manual-steps w-full h-full">
            <!-- MANUAL STEP -->

            <div
                id="manual-step-1"
                class="manual-step flex flex-col h-full"
                v-show="manualStep === 1"
            >
                <div class="modal-body">
                    <div
                        class="text-center mb-8 mx-auto flex justify-center items-center rounded-full bg-gray-100 w-14 h-14"
                    >
                        <i class="far fa-credit-card text-2xl"></i>
                    </div>

                    <p
                        class="mb-8 text-lg md:text-2xl text-center mx-auto w-full max-w-md"
                        v-if="!manualCard.card_not_found"
                    >
                        <UserCardSearch
                            v-model:input="manualCard.chosenCard"
                            :model-value="manualCard.chosenCard"
                            @cardSelected="cardSelected"
                            placeholder="Type card title"
                        />
                    </p>

                    <p
                        class="mb-8 text-base font-normal text-center"
                        v-if="!manualCard.card_not_found"
                    >
                        The last digits of my
                        <span v-if="chosenCard">
                            {{ chosenCard.issuing_bank.name }}
                        </span>
                        card are
                        <UiInput
                            id="last_four"
                            label="Last Four Digits of Card"
                            name="last_four"
                            :options="{ hideLabel: true }"
                            class="pt-1"
                        >
                            <template #input>
                                <input
                                    class="form-input text-center"
                                    v-model="manualCard.last_four"
                                    style="max-width: 7rem"
                                    type="text"
                                    placeholder="#####"
                                    v-maska
                                    data-maska="#####"
                                />
                            </template>
                        </UiInput>
                    </p>

                    <div class="pt-2">
                        <label class="flex items-center justify-center">
                            <input
                                class="form-checkbox cursor-pointer"
                                type="checkbox"
                                v-model="manualCard.card_not_found"
                                value="1"
                            />
                            <span class="ml-2 text-sm text-gray-400">
                                My card is not listed
                                <tooltip
                                    :text="'Check this option if you are unable to find your card listed in the menu above.'"
                                ></tooltip>
                            </span>
                        </label>
                    </div>
                    <transition>
                        <div
                            v-if="manualCard.card_not_found"
                            class="pt-2 mt-8 w-full"
                        >
                            <div
                                class="rounded-lg bg-gray-100 px-6 py-4 w-full md:w-3/4 mx-auto mb-10 shadow border flex flex-wrap"
                            >
                                <div class="mb-3 w-full">
                                    <h3
                                        class="text-xl mb-5 capitalize text-left"
                                    >
                                        Missing Card Request
                                    </h3>
                                </div>
                                <div
                                    class="mb-1 md:mb-3 w-full md:w-1/2 md:pr-2"
                                >
                                    <UiInput
                                        id="card_not_found_bank"
                                        name="card_not_found_bank"
                                        label="Issuing Bank"
                                        :options="{ hideLabel: true }"
                                    >
                                        <template #input>
                                            <span
                                                class="block w-full mb-2 text-sm text-left"
                                            >
                                                Issuing Bank
                                            </span>
                                            <input
                                                type="text"
                                                class="form-input w-full mb-2"
                                                v-model="
                                                    manualCard.card_not_found_bank
                                                "
                                                placeholder="e.g. American Express"
                                            />
                                        </template>
                                    </UiInput>
                                </div>
                                <div
                                    class="mb-1 md:mb-3 w-full md:w-1/2 md:pl-2"
                                >
                                    <UiInput
                                        id="card_not_found_name"
                                        name="card_not_found_name"
                                        label="Card Name"
                                        :options="{ hideLabel: true }"
                                    >
                                        <template #input>
                                            <span
                                                class="block w-full mb-2 text-sm text-left"
                                            >
                                                Card Name
                                            </span>
                                            <input
                                                type="text"
                                                class="form-input w-full"
                                                v-model="
                                                    manualCard.card_not_found_name
                                                "
                                                placeholder="e.g. The 10xTravel Card"
                                            />
                                        </template>
                                    </UiInput>
                                </div>
                            </div>
                            <div class="prose max-w-prose mx-auto text-sm">
                                <p>
                                    Submit as much info about the card in
                                    reference using the above inputs. Please be
                                    as accurate as possible in reference to the
                                    card name so that we can ensure we have the
                                    correct card.
                                </p>
                                <p>
                                    We'll then work to get this card added to
                                    our system, and you'll be notified once it's
                                    been added.
                                </p>
                            </div>
                        </div>
                    </transition>
                    <div class="w-full pt-2" v-show="errors.length > 0">
                        <Errors :errors="errors" />
                    </div>
                </div>
                <div
                    class="modal-footer flex !flex-row flex-wrap justify-between items-center bg-gray-50 mr-auto gap-2"
                >
                    <a
                        href="#connect-step-2"
                        @click.prevent="gotoStep(2)"
                        class="btn-base btn-white !w-48"
                        id="hs-wallet-manual-card-entry-back"
                    >
                        &larr; Back
                    </a>
                    <a
                        href="#manual-step-2"
                        v-if="!manualCard.card_not_found"
                        class="btn-base btn-dark !w-48"
                        @click.prevent="gotoManualStep(2)"
                        id="hs-wallet-manual-card-entry-next"
                    >
                        Next
                    </a>
                    <ActionButton
                        v-if="manualCard.card_not_found"
                        @clicked="addCard"
                        label="Submit Request"
                        :loading="addCardBtnAction"
                        classes="btn btn-orange !w-48"
                    />
                </div>
            </div>
            <!-- // END MANUAL STEP -->
            <!-- MANUAL STEP -->

            <div
                id="manual-step-2"
                class="manual-step h-full"
                v-show="manualStep === 2"
            >
                <div class="modal-body">
                    <div
                        class="mb-10 flex items-center"
                        v-if="manualChosenCard"
                    >
                        <img
                            :src="`${cdnUrl}/${manualChosenCard.card_image?.path}/_small/${manualChosenCard.card_image?.file_name}`"
                            :alt="manualChosenCard.title"
                            class="inline-block"
                            style="
                                max-width: 75px;
                                height: auto;
                                margin-right: 12px;
                            "
                            v-if="manualChosenCard.image"
                        />
                        <p class="leading-tight">
                            {{ manualChosenCard.title }}
                        </p>
                    </div>

                    <p class="mb-5 text-center">
                        <label class="flex flex-col items-center w-full">
                            <span class="block w-full mb-5 text-lg md:text-2xl">
                                When did you open this card?
                            </span>
                            <UiInput
                                id="date_opened"
                                name="date_opened"
                                label="When did you open this card?"
                                placeholder="Date opened"
                                :options="{
                                    icon: 'calendar',
                                    hideLabel: true,
                                }"
                                class="w-full"
                            >
                                <template #input>
                                    <flat-pickr
                                        :config="dateConfig"
                                        class="form-input date-picker flatpickr-input active border shadow-sm pr-9 pl-10 w-full disabled:cursor-not-allowed"
                                        v-model="manualCard.dateOpened"
                                        placeholder="Select Date ..."
                                        :disabled="manualCard.opened_long_ago"
                                    ></flat-pickr>
                                </template>
                            </UiInput>
                        </label>
                        <!-- <span class="block pt-3"> -->
                        <label
                            class="w-full flex justify-center text-left pt-5"
                        >
                            <input
                                type="checkbox"
                                value="1"
                                v-model="manualCard.opened_long_ago"
                            />
                            <span class="ml-2 text-sm leading-tight">
                                I'm not sure
                                <br />
                                <small>(it was a few years ago)</small>
                            </span>
                        </label>
                        <!-- </span> -->
                    </p>
                    <p class="text-xs text-center px-5 mb-10">
                        Choose the date you were approved for the card, not
                        application date, or when you received it in the mail.
                    </p>
                    <div class="w-full py-2 my-2" v-show="errors.length > 0">
                        <Errors :errors="errors" />
                    </div>
                </div>
                <div
                    class="modal-footer flex !flex-row flex-wrap justify-between items-center bg-gray-50 mr-auto"
                >
                    <a
                        href="#manual-step-1"
                        @click.prevent="gotoManualStep(1)"
                        class="btn-base btn-white !w-48"
                        id="hs-wallet-manual-card-entry-back"
                    >
                        &larr; Back
                    </a>
                    <a
                        href="#manual-step-3"
                        class="btn-base btn-primary !w-48"
                        @click.prevent="gotoManualStep(3)"
                        id="hs-wallet-manual-card-entry-next"
                    >
                        Next
                    </a>
                </div>
            </div>

            <!-- // END MANUAL STEP -->
            <!-- MANUAL STEP -->

            <div
                id="manual-step-3"
                class="manual-step h-full"
                v-show="manualStep === 3"
            >
                <div class="modal-body">
                    <div
                        class="mb-10 flex items-center"
                        v-if="manualChosenCard"
                    >
                        <img
                            :src="manualChosenCard.image"
                            :alt="manualChosenCard.title"
                            class="inline-block"
                            style="
                                max-width: 75px;
                                height: auto;
                                margin-right: 12px;
                            "
                            v-if="manualChosenCard.image"
                        />
                        <p class="leading-tight">
                            {{ manualChosenCard.title }}
                        </p>
                    </div>

                    <div class="mb-10 card-type-options w-full">
                        <p class="block mb-5 text-center text-lg md:text-2xl">
                            You Are
                        </p>
                        <div class="flex justify-center items-center w-full">
                            <label
                                class="mx-3 flex justify-center items-center flex-col cursor-pointer w-1/2 md:w-auto"
                            >
                                <input
                                    type="radio"
                                    class="form-radio hide"
                                    name="account_type"
                                    value="cardholder"
                                    v-model="manualCard.accountType"
                                />
                                <span
                                    class="flex justify-center items-center flex-col border-gray-300 border rounded-md text-xs font-semibold md:text-base p-3 lg:p-6 text-center w-full md:w-48 hover:bg-gray-100"
                                >
                                    <span
                                        class="flex items-center justify-center rounded-full bg-gray-300 text-xl mb-3 w-12 h-12"
                                    >
                                        <i class="fas fa-user"></i>
                                    </span>
                                    The Cardholder
                                </span>
                            </label>
                            <label
                                class="mx-3 flex justify-center items-center flex-col cursor-pointer w-1/2 md:w-auto"
                            >
                                <input
                                    type="radio"
                                    class="form-radio hide"
                                    name="account_type"
                                    value="authorized_user"
                                    v-model="manualCard.accountType"
                                />
                                <span
                                    class="flex justify-center items-center flex-col border-gray-300 border rounded-md text-xs font-semibold md:text-base p-3 lg:p-6 text-center w-full md:w-48 hover:bg-gray-100"
                                >
                                    <span
                                        class="flex items-center justify-center rounded-full bg-gray-300 text-xl mb-3 w-12 h-12"
                                    >
                                        <i class="fas fa-user-friends"></i>
                                    </span>
                                    Authorized User
                                </span>
                            </label>
                        </div>
                    </div>
                    <div class="w-full" v-show="errors.length > 0">
                        <Errors :errors="errors" />
                    </div>
                </div>
                <div
                    class="modal-footer flex !flex-row flex-wrap justify-between items-center bg-gray-50 mr-auto"
                >
                    <a
                        href="#manual-step-2"
                        @click.prevent="gotoManualStep(2)"
                        class="btn-base btn-white !w-48"
                        id="hs-wallet-manual-card-entry-back"
                    >
                        &larr; Back
                    </a>
                    <a
                        href="#manual-step-3"
                        class="btn-base btn-primary !w-48"
                        @click.prevent="gotoManualStep(4)"
                        id="hs-wallet-manual-card-entry-next"
                    >
                        Next
                    </a>
                </div>
            </div>

            <div
                id="manual-step-4"
                class="manual-step flex flex-col items-center h-full"
                v-show="manualStep === 4"
            >
                <div class="modal-body">
                    <div
                        class="mb-10 flex items-center"
                        v-if="manualChosenCard"
                    >
                        <img
                            :src="manualChosenCard.image"
                            :alt="manualChosenCard.title"
                            class="inline-block"
                            style="
                                max-width: 75px;
                                height: auto;
                                margin-right: 12px;
                            "
                            v-if="manualChosenCard.image"
                        />
                        <p class="leading-tight">
                            {{ manualChosenCard.title }}
                        </p>
                    </div>

                    <p
                        class="mb-10 text-base md:text-xl md:text-center"
                        v-if="manualChosenCard"
                    >
                        I
                        <span class="inline-block custom-select">
                            <select v-model="gottenBonus">
                                <option value="">have/have not</option>
                                <option value="Y">Have</option>
                                <option value="N">Have Not</option>
                                <option value="NA">N/A</option>
                            </select>
                        </span>
                        gotten the welcome bonus on my
                        {{ manualChosenCard.title }}.
                    </p>
                    <div
                        class="mb-4 text-base md:text-xl text-center"
                        v-if="gottenBonus === 'Y' || gottenBonus === 'N'"
                    >
                        <span class="block mb-10">
                            It {{ gottenBonus === 'Y' ? 'was' : 'is' }}
                            <UiInput
                                label="Bonus Amount"
                                id="bonus_amount"
                                name="bonus_amount"
                                :options="{
                                    hideLabel: true,
                                }"
                            >
                                <template #input>
                                    <VueNumberFormat
                                        class="form-input"
                                        v-model:value="manualCard.bonusAmount"
                                        placeholder="XXX,XXX..."
                                        step="1"
                                        min="0"
                                    ></VueNumberFormat>
                                </template>
                            </UiInput>
                            rewards points.
                        </span>
                        <p class="mb-5 text-center" v-if="gottenBonus === 'Y'">
                            <label class="flex flex-col items-center">
                                <span class="block w-full mb-3 text-lg">
                                    Date Earned
                                </span>
                                <UiInput
                                    id="date_received"
                                    name="date_received"
                                    label="Date Earned"
                                    placeholder="Date earned"
                                    :options="{
                                        icon: 'calendar',
                                        hideLabel: true,
                                    }"
                                >
                                    <template #input>
                                        <flat-pickr
                                            :config="dateConfig"
                                            class="form-input date-picker flatpickr-input active border shadow-sm pr-9 pl-10 w-full"
                                            v-model="manualCard.dateReceived"
                                            placeholder="Select Date ..."
                                        ></flat-pickr>
                                    </template>
                                </UiInput>
                            </label>
                        </p>
                        <span v-if="gottenBonus === 'N'">
                            I have until
                            <div class="relative w-64 mb-2 mx-auto mt-2">
                                <UiInput
                                    id="minimum_spend_date"
                                    name="minimum_spend_date"
                                    label="I have until"
                                    placeholder="Select date ..."
                                    :options="{
                                        icon: 'calendar',
                                        hideLabel: true,
                                    }"
                                >
                                    <template #input>
                                        <flat-pickr
                                            :config="futureDateConfig"
                                            class="form-input date-picker flatpickr-input active border shadow-sm pr-9 pl-10 w-full"
                                            v-model="manualCard.minSpendDate"
                                            placeholder="Select Date ..."
                                        ></flat-pickr>
                                    </template>
                                </UiInput>
                            </div>
                            to meet the minimum spend.
                        </span>
                    </div>
                    <div class="w-full" v-show="errors.length > 0">
                        <Errors :errors="errors" />
                    </div>
                </div>
                <div
                    class="modal-footer flex !flex-row gap-4 flex-wrap justify-between items-center bg-gray-50 mr-auto"
                >
                    <a
                        href="#manual-step-4"
                        @click.prevent="gotoManualStep(3)"
                        class="btn-base btn-white !w-48"
                        id="hs-wallet-manual-card-entry-back"
                    >
                        &larr; Back
                    </a>
                    <a
                        href="#manual-step-5"
                        class="btn-base btn-primary !w-48"
                        @click.prevent="gotoManualStep(5)"
                        id="hs-wallet-manual-card-entry-next"
                    >
                        Next
                    </a>
                </div>
            </div>

            <div
                id="manual-step-5"
                class="manual-step h-full"
                v-show="manualStep === 5"
            >
                <div class="modal-body">
                    <div
                        class="mb-10 flex items-center"
                        v-if="manualChosenCard"
                    >
                        <img
                            :src="manualChosenCard.image"
                            :alt="manualChosenCard.title"
                            class="inline-block"
                            style="
                                max-width: 75px;
                                height: auto;
                                margin-right: 12px;
                            "
                            v-if="manualChosenCard.image"
                        />
                        <p class="leading-tight">
                            {{ manualChosenCard.title }}
                        </p>
                    </div>

                    <div class="mb-10 card-type-options w-full">
                        <p class="block mb-5 text-center text-lg md:text-2xl">
                            This Card is Currently
                        </p>
                        <div class="flex justify-center items-center w-full">
                            <label
                                class="mx-3 flex justify-center items-center flex-col cursor-pointer w-1/2 md:w-auto"
                            >
                                <input
                                    type="radio"
                                    class="form-radio hide"
                                    name="account_type"
                                    value="open"
                                    v-model="manualCard.cardStatus"
                                />
                                <span
                                    class="flex justify-center items-center flex-col border-gray-300 border rounded-md text-xs font-semibold md:text-base p-3 lg:p-6 text-center w-full md:w-48 hover:bg-gray-100"
                                >
                                    <span
                                        class="flex items-center justify-center rounded-full bg-gray-300 text-xl mb-3 w-12 h-12"
                                    >
                                        <i class="far fa-check"></i>
                                    </span>
                                    Open
                                </span>
                            </label>
                            <label
                                class="mx-3 flex justify-center items-center flex-col cursor-pointer w-1/2 md:w-auto"
                            >
                                <input
                                    type="radio"
                                    class="form-radio hide"
                                    name="account_type"
                                    value="closed"
                                    v-model="manualCard.cardStatus"
                                />
                                <span
                                    class="flex justify-center items-center flex-col border-gray-300 border rounded-md text-xs font-semibold md:text-base p-3 lg:p-6 text-center w-full md:w-48 hover:bg-gray-100"
                                >
                                    <span
                                        class="flex items-center justify-center rounded-full bg-gray-300 text-xl mb-3 w-12 h-12"
                                    >
                                        <i class="far fa-times"></i>
                                    </span>
                                    Closed
                                </span>
                            </label>
                        </div>
                    </div>
                    <div class="w-full" v-show="errors.length > 0">
                        <Errors :errors="errors" />
                    </div>
                </div>
                <div
                    class="modal-footer flex !flex-row flex-wrap justify-between items-center bg-gray-50 mr-auto"
                >
                    <a
                        href="#manual-step-4"
                        @click.prevent="gotoManualStep(4)"
                        class="btn-base btn-white !w-48"
                        id="hs-wallet-manual-card-entry-back"
                    >
                        &larr; Back
                    </a>
                    <a
                        v-if="manualCard.accountType === 'cardholder'"
                        href="#manual-step-6"
                        class="btn-base btn-primary !w-48"
                        @click.prevent="gotoManualStep(6)"
                        id="hs-wallet-manual-card-entry-next"
                    >
                        Next
                    </a>
                    <ActionButton
                        v-if="manualCard.accountType === 'authorized_user'"
                        @clicked="addCard"
                        label="Add Card"
                        :loading="addCardBtnAction"
                        classes="btn-base btn-10x-orange !w-48"
                        buttonId="hs-wallet-manual-card-entry-submit"
                    />
                </div>
            </div>

            <div
                id="manual-step-6"
                class="manual-step h-full"
                v-show="manualStep === 6"
            >
                <div class="modal-body">
                    <div
                        class="mb-10 flex items-center"
                        v-if="manualChosenCard"
                    >
                        <img
                            :src="manualChosenCard.image"
                            :alt="manualChosenCard.title"
                            class="inline-block"
                            style="
                                max-width: 75px;
                                height: auto;
                                margin-right: 12px;
                            "
                            v-if="manualChosenCard.image"
                        />
                        <p class="leading-tight">
                            {{ manualChosenCard.title }}
                        </p>
                    </div>
                    <p class="mb-5 text-lg md:text-2xl text-center">
                        This current credit limit is
                    </p>
                    <p class="mb-8 text-center text-xl">
                        <UiInput
                            id="credit_limit"
                            label="Credit Limit"
                            name="credit_limit"
                            :options="{ hideLabel: true }"
                        >
                            <template #input>
                                <VueNumberFormat
                                    class="form-input"
                                    v-model:value="manualCard.creditLimit"
                                    :options="{ prefix: '$' }"
                                    placeholder="$X,XXX..."
                                    min="0"
                                ></VueNumberFormat>
                            </template>
                        </UiInput>
                    </p>
                </div>
                <div
                    class="modal-footer flex !flex-row flex-wrap justify-between md:justify-end items-center bg-gray-50 mr-auto"
                >
                    <div
                        class="w-full flex justify-center"
                        v-show="errors.length > 0"
                    >
                        <Errors :errors="errors" />
                    </div>
                    <a
                        href="#connect-step-5"
                        @click.prevent="gotoManualStep(5)"
                        class="btn-base btn-white !w-48"
                        id="hs-wallet-manual-card-entry-back"
                    >
                        &larr; Back
                    </a>

                    <ActionButton
                        @clicked="addCard"
                        label="Add Card"
                        :loading="addCardBtnAction"
                        classes="btn-base btn-10x-orange !w-48"
                        buttonId="hs-wallet-manual-card-entry-submit"
                    />
                </div>
            </div>
            <!-- // END MANUAL STEP -->
        </div>
        <!-- // END MANUAL STEPS -->
    </div>
</template>

<script>
import FlatPickr from 'vue-flatpickr-component';
import Errors from '@/components/errors.vue';
import ActionButton from '@/components/action-button.vue';
import Tooltip from '@/components/tooltip.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import UserCardSearch from '@/components/ui/user-card-search.vue';

import { vMaska } from 'maska/vue';
import { useWalletStore } from '@/store/wallet.js';

export default {
    props: {
        accountUserForCard: Number,
        wallet: Object,
        cards: Array,
        banks: Array,
    },
    directives: { maska: vMaska },
    components: {
        Errors,
        ActionButton,
        FlatPickr,
        Tooltip,
        UiInput,
        UserCardSearch,
    },
    setup() {
        const walletStore = useWalletStore();
        return { walletStore };
    },
    data: () => ({
        errors: [],
        manualStep: 1,
        addCardBtnAction: false,
        gottenBonus: '',
        dateConfig: {
            dateFormat: 'm/d/Y',
            maxDate: 'today',
        },
        futureDateConfig: {
            dateFormat: 'm/d/Y',
            minDate: 'today',
        },
        manualCard: {
            chosenCard: null,
            dateOpened: '',
            accountType: '',
            bonusAmount: '',
            minSpendDate: '',
            bonusReceived: '',
            dateReceived: '',
            cardStatus: '',
            dateClosed: '',
            creditLimit: 0,
            dateBonusEarned: '',
            currentRewardsBalance: '',
            institution_code: '',
            card_not_found_name: '',
            card_not_found_bank: '',
            card_not_found: false,
            opened_long_ago: false,
        },
        manualChosenCard: null,
    }),
    watch: {
        'walletStore.selectedCard': {
            immediate: true,
            handler(newCard) {
                if (newCard && newCard.card_match_request) {
                    // Find the unresolved card
                    const unresolvedCard =
                        this.walletStore.wallet.unresolved_cards.find(
                            (card) => card.connected_account_id === newCard.id
                        );
                    if (unresolvedCard) {
                        this.manualCard.card_not_found = true;
                        this.manualCard.card_not_found_name =
                            unresolvedCard.card_not_found_name;
                        this.manualCard.card_not_found_bank =
                            unresolvedCard.issuing_bank;
                    }
                }
            },
        },
        'manualCard.card_not_found'(newVal, oldVal) {
            if (newVal) {
                const existingBank = this.manualCard.card_not_found_bank;
                const existingName = this.manualCard.card_not_found_name;

                this.manualCard = {
                    chosenCard: null,
                    dateOpened: '',
                    accountType: '',
                    bonusAmount: '',
                    minSpendDate: '',
                    bonusReceived: '',
                    dateReceived: '',
                    cardStatus: '',
                    dateClosed: '',
                    creditLimit: 0,
                    dateBonusEarned: '',
                    currentRewardsBalance: '',
                    institution_code: '',
                    card_not_found: true,
                    opened_long_ago: false,
                    card_not_found_name: existingName,
                    card_not_found_bank: existingBank,
                };
            }
        },
    },
    computed: {
        chosenCard() {
            return this.cards.find((el) => {
                return (
                    el.issuing_bank.institution_code ===
                    this.manualCard.institution_code
                );
            });
        },
        filteredCards() {
            return this.cards.filter((el) => {
                return (
                    el.issuing_bank.institution_code ===
                    this.manualCard.institution_code
                );
            });
        },
        cdnUrl() {
            return window.cdnUrl;
        },
    },
    methods: {
        gotoStep(step) {
            this.errors = [];
            this.$emit('gotoStep', parseInt(step));
        },
        gotoManualStep(step) {
            console.log(step);
            this.errors = [];
            const stepsPercent = {
                1: 40,
                2: 50,
                3: 60,
                4: 70,
                5: 80,
                6: 90,
                7: 100,
            };

            if (this.manualStep === 1 && step === 2) {
                if (
                    !this.manualCard.chosenCard &&
                    !this.manualCard.card_not_found
                ) {
                    this.errors.push({ message: 'Please choose a card.' });
                }
                if (
                    this.manualCard.card_not_found &&
                    this.manualCard.card_not_found_name === ''
                ) {
                    this.errors.push({
                        message: 'Please enter the card suggestion.',
                    });
                }
            }

            if (this.manualStep === 2 && step === 3) {
                if (
                    this.manualCard.dateOpened === '' &&
                    !this.manualCard.opened_long_ago
                ) {
                    this.errors.push({
                        message: 'Please enter the date opened.',
                    });
                }
                console.log(this.errors);
            }

            if (this.manualStep === 3 && step === 4) {
                if (this.manualCard.accountType === '') {
                    this.errors.push({
                        message: 'Please choose the cardholder type.',
                    });
                }

                if (this.manualCard.accountType === 'authorized_user') {
                    this.$emit('setProgress', 100);
                    this.manualStep = 5;
                    return;
                }
            }

            if (
                this.manualCard.accountType === 'authorized_user' &&
                this.manualStep === 5 &&
                step === 4
            ) {
                this.manualStep = 3;
                this.$emit('setProgress', 60);
                return;
            }

            if (this.manualStep === 4 && step === 5) {
                if (this.gottenBonus === '') {
                    this.errors.push({
                        message:
                            'Please indicate whether you have received the bonus.',
                    });
                }
                if (
                    this.gottenBonus === 'Y' &&
                    this.manualCard.bonusAmount === ''
                ) {
                    this.errors.push({
                        message: 'Please enter your bonus amount.',
                    });
                }
                if (
                    this.gottenBonus === 'N' &&
                    this.manualCard.minSpendDate === ''
                ) {
                    this.errors.push({
                        message:
                            'Please choose your minimum spend deadline date.',
                    });
                }
            }

            if (this.manualStep === 5 && step === 6) {
                if (this.manualCard.cardStatus === '') {
                    this.errors.push({
                        message: "Please choose the card's current status.",
                    });
                }
            }

            if (this.errors.length === 0) {
                this.$emit('setProgress', stepsPercent[step]);
                this.manualStep = step;
            }
        },
        cardSelected(card) {
            this.manualChosenCard = card;
            this.manualCard.institution_code = card.issuing_bank.institution_code;
            this.manualCard.chosenCard = card.id;
        },
        addCard() {
            this.errors = [];
            this.addCardBtnAction = true;

            if (
                this.manualCard.creditLimit === '' &&
                this.manualCard.accountType === 'cardholder'
            ) {
                this.errors.push({ message: 'Please enter the credit limit.' });
                this.addCardBtnAction = false;
            }
            if (
                this.manualCard.card_not_found &&
                this.manualCard.card_not_found_name === ''
            ) {
                this.errors.push({
                    message: 'Please enter the card suggestion.',
                });
            }

            if (
                this.manualCard.card_not_found &&
                this.manualCard.card_not_found_bank === ''
            ) {
                this.errors.push({
                    message: 'Please enter the Issuing Bank suggestion.',
                });
            }

            if (this.errors.length > 0) {
                this.addCardBtnAction = false;
            }

            if (this.errors.length === 0) {
                this.manualCard.bonusReceived = this.gottenBonus;

                let data = {
                    user: this.accountUserForCard,
                    manualCard: this.manualCard,
                };

                ApiService.apiCall(
                    '/wallets/' + this.wallet.id + '/cards',
                    'post',
                    data
                )
                    .then((response) => {
                        if (this.manualCard.card_not_found) {
                            this.$notify(
                                {
                                    group: 'success',
                                    title: 'Request Sent',
                                    text: "Your Missing Card Request was successfully submitted. You'll hear from us soon.",
                                },
                                8000
                            );
                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                woopra.track('card-added-my10x', {
                                    datetime: new Date(),
                                    cardName: 'Missing card request',
                                    uuid: localStorage.getItem('uuid'),
                                });
                            }
                        } else {
                            this.$notify(
                                {
                                    group: 'success',
                                    title: 'Success',
                                    text: 'Card added to Wallet!',
                                },
                                8000
                            );
                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                woopra.track('card-added-my10x', {
                                    datetime: new Date(),
                                    cardName: this.manualChosenCard.title,
                                    uuid: localStorage.getItem('uuid'),
                                });
                            }
                        }
                        this.addCardBtnAction = false;

                        this.manualStep = 1;
                        this.gottenBonus = '';
                        this.manualCard = {
                            chosenCard: '',
                            dateOpened: '',
                            accountType: '',
                            bonusAmount: '',
                            minSpendDate: '',
                            bonusReceived: '',
                            dateReceived: '',
                            cardStatus: '',
                            dateClosed: '',
                            creditLimit: 0,
                            dateBonusEarned: '',
                            currentRewardsBalance: '',
                            institution_code: '',
                            card_not_found: false,
                            card_not_found_name: '',
                            opened_long_ago: false,
                        };

                        this.$emit('finished');
                    })
                    .catch((response) => {
                        this.$root.handleHttpStatus(response);
                        this.addCardBtnAction = false;
                        this.errors.push({
                            message: response?.response?.data?.message,
                        });
                    });
            }
        },
    },
    created() {},
    mounted() {},
};
</script>
<style lang="scss" scoped>
#manual-card-section {
    width: 100%;
}
.manual-steps {
    //position: relative;
    display: flex;
    flex-direction: column;
}
.manual-step {
    width: 100%;
    //position: absolute;
    //top: 0;
    //left: 0;
}
.fadeInOut-enter-active,
.fadeInOut-leave-active {
    transition: opacity 0.8s;
    opacity: 1;
}
.fadeInOut-enter,
.fadeInOut-leave-to {
    opacity: 0;
}
</style>
